// src/contexts/sector/SectorFundamentalsProvider.tsx
/**
 * SectorFundamentalsProvider - Centralizes sector fundamentals data fetching
 * 
 * Provides historical data for Fees, DAU, TxCount, TVL, Volume, MarketCap
 * Used by both Fundamentals and Volume tabs.
 */

import React, { createContext, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTopSectors } from "@/hooks/dashboard/useTopSectors";
import { categoryHistoryCompactQueryOptions } from "@/services/queries/dashboards/categoriesQueries";
import { MetricKeys } from "@/constants/metrics";
import type { CategoryHistoryCompactResponse } from "@/types/dashboards/categories";
import type { LeafCategorySnapshot } from "@/hooks/dashboard/useLeafCategorySnapshots";

// ============================================================================
// TYPES
// ============================================================================

export interface Sector {
  id: number;
  name: string;
  code: string;
  mcap: number;
}

export interface SectorFundamentalsContextValue {
  /** Top sectors being tracked */
  sectors: Sector[];
  
  /** Leaf category snapshots (with current values, ratios, rollups) */
  leaves: LeafCategorySnapshot[];
  
  /** Raw API responses indexed by sector ID - for timeseries charts */
  historyDataById: Record<number, CategoryHistoryCompactResponse>;
  
  /** Loading states */
  isLoading: boolean;
  isLoadingHistory: boolean;
  isError: boolean;
  error: Error | null;
  
  /** Refetch all data */
  refetch: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const SectorFundamentalsContext = createContext<SectorFundamentalsContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface SectorFundamentalsProviderProps {
  /** Number of top sectors to include */
  topN?: number;
  children: React.ReactNode;
}

export const SectorFundamentalsProvider: React.FC<SectorFundamentalsProviderProps> = ({
  topN = 15,
  children,
}) => {
  // Get top sectors and leaf snapshots
  const { topSectors, leaves, isLoading: sectorsLoading } = useTopSectors({
    count: topN,
  });
  
  // Batch fetch fundamental metrics for all sectors
  // Include all metrics needed by both Fundamentals and Volume tabs
  const queries = useQueries({
    queries: topSectors.map(sector => ({
      ...categoryHistoryCompactQueryOptions(sector.id, [
        MetricKeys.Fees,
        MetricKeys.DAU,
        MetricKeys.TxCount,
        MetricKeys.TVL,
        MetricKeys.Volume,
        MetricKeys.MarketCap,
      ]),
      enabled: sector.id > 0,
      staleTime: 10 * 60 * 1000,
    })),
  });
  
  const value = useMemo<SectorFundamentalsContextValue>(() => {
    // Build history data map
    const historyDataById: Record<number, CategoryHistoryCompactResponse> = {};
    topSectors.forEach((sector, idx) => {
      const data = queries[idx]?.data;
      if (data) {
        historyDataById[sector.id] = data;
      }
    });
    
    const isLoadingHistory = queries.some(q => q.isLoading);
    const isLoading = sectorsLoading || isLoadingHistory;
    const isError = queries.some(q => q.isError);
    const error = queries.find(q => q.error)?.error as Error | null;
    
    return {
      sectors: topSectors,
      leaves,
      historyDataById,
      isLoading,
      isLoadingHistory,
      isError,
      error,
      refetch: () => {
        queries.forEach(q => q.refetch());
      },
    };
  }, [topSectors, leaves, queries, sectorsLoading]);
  
  return (
    <SectorFundamentalsContext.Provider value={value}>
      {children}
    </SectorFundamentalsContext.Provider>
  );
};

export { SectorFundamentalsContext };

